'use client';

import React, { useEffect, useMemo, useState } from 'react';
import type { Quote } from '@/types';
import { X } from 'lucide-react';
import { outboundService } from '@/services/outboundService';
import { quoteApi } from '@/services/quoteApi';

type QuotePreviewProps = {
  quote: Quote;
  onClose: () => void;
  onEdit: () => void;
  onSend?: () => void;
  onRefreshZoho?: () => Promise<void>;
  onViewThread?: () => void;
};

const QuotePreview: React.FC<QuotePreviewProps> = ({ quote, onClose, onEdit, onSend, onRefreshZoho, onViewThread }) => {
  const legs = Array.isArray(quote.legs) ? quote.legs : [];
  const [pdfTemplateId, setPdfTemplateId] = useState<string>('');
  const [includeBreakdown, setIncludeBreakdown] = useState(true);
  const [printing, setPrinting] = useState(false);
  const [showPrintDialog, setShowPrintDialog] = useState(false);
  const [pdfTemplates, setPdfTemplates] = useState<{ id: string; name: string }[]>([]);
  const [refreshingZoho, setRefreshingZoho] = useState(false);

  useEffect(() => {
    let isMounted = true;
    const loadTemplates = async () => {
      try {
        const state = await outboundService.getState();
        const templates = Array.isArray(state.templates) ? state.templates : [];
        const pdfs = templates.filter(t => t.type === 'PDF').map(t => ({ id: t.id, name: t.name }));
        if (isMounted) {
          setPdfTemplates(pdfs);
          if (!pdfTemplateId && pdfs.length > 0) {
            setPdfTemplateId(pdfs[0].id);
          }
        }
      } catch {
        // ignore fetch errors
      }
    };
    loadTemplates();
    return () => {
      isMounted = false;
    };
  }, [pdfTemplateId]);

  const canPrint = useMemo(() => Boolean(pdfTemplateId), [pdfTemplateId]);

  const handlePrint = async () => {
    if (!quote.id || !pdfTemplateId) return;
    try {
      setPrinting(true);
      const pdfBlob = await quoteApi.printQuote(quote.id, { pdfTemplateId, includeBreakdown });
      const url = window.URL.createObjectURL(pdfBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `quote-${quote.quoteNumber || quote.id}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } finally {
      setPrinting(false);
    }
  };

  const handleRefreshZoho = async () => {
    if (!onRefreshZoho || !quote.zohoEstimateId) return;
    try {
      setRefreshingZoho(true);
      await onRefreshZoho();
    } finally {
      setRefreshingZoho(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-6">
      <div className="w-full max-w-4xl rounded-2xl bg-gradient-to-br from-white via-slate-50 to-white shadow-2xl border border-slate-200/50 overflow-hidden">
        <div className="flex items-center justify-between px-6 py-5 border-b border-slate-200/50">
          <div>
            <p className="text-[11px] font-bold uppercase tracking-[0.25em] text-indigo-600">Quote Preview</p>
            <h3 className="text-2xl font-bold text-slate-900 mt-1">
              {quote.customerName || 'Charter Quote'}
            </h3>
            <div className="mt-1 text-xs text-slate-500">
              Booking: {quote.bookingId || 'Pending'} · PNR: {quote.pnrCode || 'Pending'} · Date: {quote.date}
            </div>
            {quote.issuedBy && (
              <div className="mt-1 text-xs text-slate-500">
                Issued by: {quote.issuedBy}
              </div>
            )}
          </div>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={onEdit}
              className="btn-action btn-ghost btn-sm rounded-lg px-4 py-2 text-xs font-semibold uppercase tracking-wider"
            >
              Edit
            </button>
            {quote.zohoEstimateId && onRefreshZoho && (
              <button
                type="button"
                onClick={handleRefreshZoho}
                className="btn-action badge badge-success text-[10px] px-3 py-1.5"
                disabled={refreshingZoho}
              >
                {refreshingZoho ? 'Refreshing…' : 'Refresh Zoho'}
              </button>
            )}
            {onViewThread && (
              <button
                type="button"
                onClick={onViewThread}
                className="btn-action btn-outline badge badge-zoho text-[10px] px-3 py-1.5"
              >
                Email Thread
              </button>
            )}
            <button
              type="button"
              onClick={() => setShowPrintDialog(true)}
              className="btn-action btn-outline badge badge-success text-[10px] px-3 py-1.5"
            >
              Print PDF
            </button>
            {onSend && (
              <button
                type="button"
                onClick={onSend}
                className="btn-action btn-premium rounded-lg px-4 py-2 text-xs font-semibold uppercase"
              >
                Send
              </button>
            )}
            <button
              type="button"
              onClick={onClose}
              className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-slate-100 text-slate-500 hover:text-slate-900"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-6 p-6">
          <div className="space-y-4">
            <div className="rounded-xl border border-slate-200/50 bg-gradient-to-br from-slate-50 to-white p-4 shadow-sm">
              <div className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">Mission Overview</div>
              <div className="flex flex-wrap gap-4 text-sm text-slate-700">
                <span>Aircraft: {quote.aircraftModel || quote.tailNumber || 'TBD'}</span>
                <span>Status: {quote.status}</span>
                <span>Legs: {legs.length}</span>
                <span>Block: {(quote.totalBlockTimeMinutes / 60).toFixed(1)} hrs</span>
                {quote.zohoEstimateNumber && (
                  <span className="badge badge-zoho badge-pill">Zoho Estimate: {quote.zohoEstimateNumber}</span>
                )}
              </div>
            </div>

            <div className="rounded-xl border border-slate-200/50 bg-gradient-to-br from-slate-50 to-white p-4 shadow-sm">
              <div className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-3">Flight Sectors</div>
              <div className="space-y-3">
                {legs.map((leg, idx) => (
                  <div key={leg.id || idx} className="rounded-lg border border-slate-200 px-3 py-2">
                    <div className="flex items-center justify-between text-sm font-semibold text-slate-900">
                      <span>
                        {leg.from || leg.departureICAO} → {leg.to || leg.arrivalICAO}
                      </span>
                      <span className="text-slate-500">
                        {leg.paxCount || 0} pax · {leg.blockTimeMinutes || 0} min
                      </span>
                    </div>
                    <div className="text-xs text-slate-500 mt-1">
                      ETD: {leg.etd || '—'} · ETA: {leg.eta || '—'}
                    </div>
                  </div>
                ))}
                {legs.length === 0 && (
                  <div className="text-sm text-slate-500">No legs configured.</div>
                )}
              </div>
            </div>
          </div>

          <div className="rounded-2xl bg-slate-900 text-white p-5 space-y-4">
            <div className="text-[10px] uppercase tracking-[0.3em] text-emerald-300">Pricing Summary</div>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-slate-300">Base Charter</span><span>${Math.round(quote.costs?.base || 0).toLocaleString()}</span></div>
              <div className="flex justify-between">
                <span className="text-slate-300">Aircraft Taxes</span>
                <span>
                  ${Math.round(
                    (quote.costs?.landingFees || 0) +
                      (quote.costs?.handlingFees || 0) +
                      (quote.costs?.navigationFees || 0) +
                      (quote.costs?.aircraftTax || 0)
                  ).toLocaleString()}
                </span>
              </div>
              <div className="flex justify-between"><span className="text-slate-300">Taxes</span><span>${Math.round(quote.costs?.taxes || 0).toLocaleString()}</span></div>
              <div className="flex justify-between"><span className="text-slate-300">Safety & Security</span><span>${Math.round(quote.costs?.safetySecurity || 0).toLocaleString()}</span></div>
              <div className="flex justify-between"><span className="text-slate-300">Extra Services</span><span>${Math.round(quote.costs?.extraServices || 0).toLocaleString()}</span></div>
            </div>
            <div className="border-t border-slate-700/60 pt-3">
              <div className="flex items-end justify-between">
                <span className="text-xs uppercase tracking-wider text-slate-400">Contract Value</span>
                <span className="text-xl font-bold text-emerald-300">${Math.round(quote.totalUSD).toLocaleString()}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      {showPrintDialog && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/40 p-4">
          <div className="w-full max-w-md rounded-2xl bg-white border border-slate-200 shadow-xl p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h4 className="text-sm font-semibold text-slate-900">Select PDF Template</h4>
              <button
                type="button"
                onClick={() => setShowPrintDialog(false)}
                className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-slate-100 text-slate-500 hover:text-slate-900"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="space-y-3">
              <label className="text-xs uppercase tracking-wider text-slate-400">Template</label>
              <select
                className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-700"
                value={pdfTemplateId}
                onChange={e => setPdfTemplateId(e.target.value)}
              >
                {pdfTemplates.length === 0 && <option value="">No PDF templates</option>}
                {pdfTemplates.map(t => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
              </select>
              <label className="flex items-center gap-2 text-xs text-slate-500">
                <input
                  type="checkbox"
                  checked={includeBreakdown}
                  onChange={e => setIncludeBreakdown(e.target.checked)}
                />
                Full breakdown
              </label>
            </div>
            <div className="flex items-center justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={() => setShowPrintDialog(false)}
                className="btn-sm btn-outline"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={!canPrint || printing}
                onClick={async () => {
                  await handlePrint();
                  setShowPrintDialog(false);
                }}
                className="btn-sm border border-emerald-200 bg-emerald-50 text-emerald-700 disabled:opacity-50"
              >
                {printing ? 'Printing…' : 'Print PDF'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default QuotePreview;
